import { useEffect, useState } from "react";

/**
 * FocusModeIndicator - WCAG 2.4.11 Focus Appearance
 *
 * Shows a visual indicator when user is navigating with keyboard.
 * Appears on first Tab press, hides on mouse click.
 * Helps sighted keyboard users understand they're in keyboard navigation mode.
 */
export const FocusModeIndicator: React.FC = () => {
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);

  useEffect(() => {
    // Show indicator when Tab key is pressed
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        setIsKeyboardMode(true);
      }
    };

    // Hide indicator on mouse interaction
    const handleMouseDown = () => {
      setIsKeyboardMode(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  if (!isKeyboardMode) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg"
    >
      <div className="flex items-center gap-2">
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <span>Keyboard navigation active</span>
        <kbd className="ml-1 rounded bg-blue-500 px-1.5 py-0.5 text-xs">
          Tab
        </kbd>
        <span className="text-blue-200">to navigate</span>
      </div>
    </div>
  );
};

export default FocusModeIndicator;
